import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { overviewWidgetsPermissionsArray } from '../../../App.util';
import { LANGUAGE_SWITCH_EVENT, SIGN_IN_EVENT, THEME_SWITCH_EVENT } from '../../../constants';
import { corporateBrandIcons } from '../../../constants/brandMapping';
import { LocalStorageKey } from '../../../constants/localStorageKey';
import { useThemeContext } from '../../../global-state/themeContext';
import { useLocalStorage, useProfile, useStoreInformation, useUser } from '../../../hooks';
import { useCurrencyTools } from '../../../hooks/useCurrencyTools';
import useEventTracker from '../../../hooks/useEventTracker';
import { CurrencyConverter } from '../../../pages/currencyConverter/CurrencyConverter';
import { LogoutModal } from '../../../pages/logout-modal/LogoutModal';
import { NavigateToSwitchStoreModal } from '../../../pages/select-store/navigate-to-switch-store-modal/NavigateToSwitchStoreModal';
import { SwitchStoreModal } from '../../../pages/select-store/switch-store-modal/SwitchStoreModal';
import { muiTheme } from '../../../theme';
import { headerTranslations, multiStoreMenuItemTranslation } from '../../../translations';
import { Translation, isFailureResponse } from '../../../utils';
import { getFlagUrlFromLanguage } from '../../../utils/language';
import { Icon, Icons, Typography } from '../../atoms';
import { RowFlexContainer } from '../../atoms/containerV2/ContainerV2';
import { DropdownMenu, IconWithText } from '../../molecules';
import { MessageDropdown } from './header-menus';
import { LanguageItem, StyledDivider, StyledHeaderContainer, StyledHeaderMenu, StyledIcon } from './Header.styles';

export type BorderType = 'solid' | 'dashed' | 'dotted';

export type Props = {
  onOpenNav?: () => void;
  currentPathNameTranslation: Translation;
  borderType?: BorderType;
  isNonSignPage?: boolean;
};

const { switchLanguage } = multiStoreMenuItemTranslation;
const { signin } = headerTranslations;

export const Header: FC<React.PropsWithChildren<Props>> = observer(
  ({ onOpenNav, currentPathNameTranslation, borderType = 'solid', isNonSignPage = false }) => {
    const { key, defaultValue } = currentPathNameTranslation;
    const { currencyCode: storeCurrency } = useStoreInformation();

    const { selectedCurrency } = useCurrencyTools();

    const { setItem, getItem } = useLocalStorage();
    const { mode, toggleTheme } = useThemeContext();
    const { t, i18n } = useTranslation();
    const {
      get: { currentStoreId, storesDetails, profile, stores, userPermissions },
    } = useUser();
    const { updateProfile } = useProfile();
    const navigate = useNavigate();
    const [storeIdToSwitchTo, setStoreIdToSwitchTo] = useState<string>('');
    const [redirectUrl, setRedirectUrl] = useState<string>('');
    const [isNavigateModalOpen, setIsNavigateModalOpen] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [currentSettings, setCurrentSettings] = useState(profile.settings);

    const canViewCurrencyConverter =
      userPermissions.currencyConverter.canRead && !!storeCurrency && !!selectedCurrency;

    const trackEvent = useEventTracker();

    const isDesktop = useMediaQuery(muiTheme.breakpoints.up('desktopLarge'));
    const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

    const displayName = new Intl.DisplayNames(i18n.language, { type: 'language' });

    const defaultLanguage = currentSettings?.defaultLanguage || i18n.language;

    useEffect(() => {
      if (currentSettings?.defaultLanguage) {
        i18n.changeLanguage(currentSettings.defaultLanguage);
      }
    }, [currentSettings?.defaultLanguage, i18n]);

    const handleSettingsChange = async (settings: { darkMode?: boolean; defaultLanguage?: string }) => {
      setCurrentSettings((prev) => ({ ...prev, ...settings }));
      // Apply changes immediately to UI
      if (settings.darkMode !== undefined) toggleTheme();
      if (settings.defaultLanguage) i18n.changeLanguage(settings.defaultLanguage);

      // Persist asynchronously
      try {
        const response = await updateProfile(profile.upn, [
          { op: 'add', path: '/settings', value: { ...currentSettings, ...settings } },
        ]);
        if (isFailureResponse(response)) {
          console.warn('Failed to persist profile settings to backend');
        }
      } catch (error) {
        console.warn('Network error while persisting settings', error);
      }
    };

    const getLanguages = () => {
      const supportedLanguages = i18n.options.supportedLngs;
      if (supportedLanguages) {
        return supportedLanguages
          .filter((language) => language !== 'cimode')
          .sort((a, b) => {
            a.localeCompare(b);
            const aLanguage = displayName.of(a);
            const bLanguage = displayName.of(b);

            if (!aLanguage || !bLanguage) return 0;

            return aLanguage < bLanguage ? -1 : 1;
          })
          .map((language) => {
            let languageDisplayName = displayName.of(language);
            if (language === 'es-MX') {
              languageDisplayName = displayName.of('es-419') ?? 'Latin American Spanish';
            }
            return {
              key: language,
              item: (
                <LanguageItem>
                  <img width={28} height={28} src={getFlagUrlFromLanguage(language)} alt={language} />
                  <Typography>{languageDisplayName}</Typography>
                </LanguageItem>
              ),
            };
          });
      }
      return [];
    };

    // Get the corporateBrandId from the current store details
    const corporateId = stores.find(({ storeId }) => storeId === currentStoreId)?.corporateBrandId;
    const brandIcon = corporateId ? corporateBrandIcons[corporateId] : Icons.HM_LOGO;

    const isStoreView = getItem(LocalStorageKey.SelectedView) === 'store';

    const canViewArea = overviewWidgetsPermissionsArray.some((widget) => userPermissions[widget]?.canRead);

    const onAreaSelect = () => {
      setItem(LocalStorageKey.SelectedView, 'area' as string);
      navigate('/home');
    };

    return (
      <StyledHeaderContainer data-testid="header" borderType={borderType}>
        <RowFlexContainer
          justifyContent="between"
          alignItems="center"
          width="100%"
          padding={isTablet ? [4] : [4, 8]}
        >
          {!isDesktop && (
            <>
              <Icon data-testid="menuIcon" onClick={onOpenNav} icon={Icons.MENU} size="small" margin={[0, 1]} />
              <RowFlexContainer padding={[0, 0, 0, 1]} alignItems="center">
                <StyledIcon
                  data-testid="homeIcon"
                  onClick={() => (isNonSignPage ? null : navigate(`/home`))}
                  icon={brandIcon}
                />
                {!isMobile && (
                  <>
                    <StyledDivider orientation="vertical" />
                    <Typography type="h4" padding={isTablet ? [1, 4] : [1]}>
                      {t(key, defaultValue)}
                    </Typography>
                  </>
                )}
              </RowFlexContainer>
            </>
          )}
          {isDesktop && <Typography type="h4">{t(key, defaultValue)}</Typography>}
          <StyledHeaderMenu data-testid="header-menu">
            <Icon
              data-testid={mode === 'dark' ? 'theme-icon-sun' : 'theme-icon-moon'}
              icon={mode === 'dark' ? Icons.SUN : Icons.MOON}
              margin={[0, 2]}
              onClick={() => {
                handleSettingsChange({ darkMode: mode !== 'dark' });
                trackEvent(THEME_SWITCH_EVENT, 'click', [
                  { name: 'theme', stringValue: mode === 'dark' ? 'light' : 'dark' },
                ]);
              }}
            />
            <DropdownMenu
              title={t(switchLanguage.key, switchLanguage.defaultValue)}
              dropdownLabel={''}
              menuItems={getLanguages()}
              initialSelected={[defaultLanguage]}
              onSelect={(selectedItemKey) => {
                handleSettingsChange({ defaultLanguage: selectedItemKey[0] });
                trackEvent(LANGUAGE_SWITCH_EVENT, 'click', [
                  { name: 'language', stringValue: selectedItemKey[0] },
                ]);
              }}
              menuPosition="left"
              backgroundColor={{ hover: 'baseGray', selected: 'primary', selectedHover: 'secondaryTextGray' }}
              buttonContentProps={{
                iconOptions: {
                  customIcon: (
                    <img
                      width={28}
                      height={28}
                      src={getFlagUrlFromLanguage(defaultLanguage)}
                      alt={defaultLanguage}
                    />
                  ),
                },
              }}
              isOnlyIcon
            />
            {canViewCurrencyConverter && !isStoreView && <CurrencyConverter />}

            {canViewArea && isStoreView && (
              <Icon data-testid="switch-area" icon={Icons.MAP} margin={[0, 2]} onClick={onAreaSelect} />
            )}

            {storesDetails.length > 1 && currentStoreId && (
              <Icon
                data-testid="switch-store"
                icon={Icons.STORE}
                margin={[0, 2]}
                onClick={() => {
                  setIsNavigateModalOpen(true);
                }}
              />
            )}

            {!isNonSignPage && (
              <MessageDropdown
                onStoreChange={(id, url) => {
                  setStoreIdToSwitchTo(id);
                  setRedirectUrl(url);
                }}
              />
            )}

            {!isNonSignPage ? (
              <Icon
                data-testid="sign-out"
                icon={Icons.SIGNOUT}
                onClick={() => setShowLogoutModal(true)}
                margin={[0, 2]}
              />
            ) : (
              <IconWithText
                data-testid="signIn"
                label={<Typography type="body3">{t(signin.key, signin.defaultValue)}</Typography>}
                onClick={() => {
                  window.location.replace('/home'); // Set the URL & reload the page taking to microsoft login
                  trackEvent(SIGN_IN_EVENT, 'click', [{ name: 'sign_in', stringValue: 'true' }]);
                }}
                padding={[0, 2]}
              />
            )}
          </StyledHeaderMenu>
          {storeIdToSwitchTo && (
            <SwitchStoreModal
              isOpen={true}
              changedStoreId={storeIdToSwitchTo}
              redirectUrl={redirectUrl}
              onClose={() => setStoreIdToSwitchTo('')}
            />
          )}

          {isNavigateModalOpen && (
            <NavigateToSwitchStoreModal isOpen={true} onClose={() => setIsNavigateModalOpen(false)} />
          )}

          {showLogoutModal && <LogoutModal isOpen={true} onClose={() => setShowLogoutModal(false)} />}
        </RowFlexContainer>
      </StyledHeaderContainer>
    );
  },
);
